import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  AlertCircle,
  XCircle,
  PlayCircle,
  Eye,
  Check,
  X,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Zap,
  Sparkles,
  Shield,
  Activity,
  ChevronRight,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

interface Recommendation {
  id: string;
  action: 'PROMOTE' | 'TEST' | 'EXCLUDE';
  keyword?: {
    text: string;
    campaignName: string;
    currentScore: number;
  };
  reason: string;
  changes: Array<{
    type: string;
    description: string;
    currentValue?: any;
    newValue?: any;
  }>;
  riskLevel: 'low' | 'medium' | 'high';
  expectedImpact: {
    costDeltaPct: number;
    convDeltaPct: number;
    cpaDeltaPct: number;
  };
  confidence: number;
}

interface Props {
  recommendations: Recommendation[];
  loading: boolean;
  onApply: (recommendationIds: string[], dryRun: boolean) => Promise<void>;
}

const RecommendationsPanel: React.FC<Props> = ({ recommendations = [], loading, onApply }) => {
  const [selectedRecos, setSelectedRecos] = useState<Set<string>>(new Set());
  const [applyingRecos, setApplyingRecos] = useState(false);
  const [showDryRunResult, setShowDryRunResult] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'promote' | 'test' | 'exclude'>('all');

  // Debug log
  useEffect(() => {
    console.log('📊 RecommendationsPanel received:', {
      count: recommendations.length,
      recommendations: recommendations.slice(0, 3),
      loading
    });
  }, [recommendations, loading]);

  const filteredRecommendations = recommendations.filter(r => {
    if (filter === 'all') return true;
    return r.action.toLowerCase() === filter;
  });

  const stats = {
    promote: recommendations.filter(r => r.action === 'PROMOTE').length,
    test: recommendations.filter(r => r.action === 'TEST').length,
    exclude: recommendations.filter(r => r.action === 'EXCLUDE').length,
    totalImpact: recommendations.reduce((acc, r) => {
      if (r.expectedImpact && r.expectedImpact.costDeltaPct) {
        return acc + Math.abs(r.expectedImpact.costDeltaPct);
      }
      return acc;
    }, 0)
  };

  const handleSelectReco = (id: string) => {
    const newSelected = new Set(selectedRecos);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecos(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRecos.size === filteredRecommendations.length) {
      setSelectedRecos(new Set());
    } else {
      setSelectedRecos(new Set(filteredRecommendations.map(r => r.id)));
    }
  };

  const handleDryRun = async () => {
    if (selectedRecos.size === 0) return;
    
    setApplyingRecos(true);
    try {
      // Przykładowy rezultat dry run
      const mockResult = {
        changes: Array.from(selectedRecos).map(id => {
          const reco = recommendations.find(r => r.id === id);
          return {
            keyword: reco?.keyword?.text,
            action: reco?.action,
            changes: reco?.changes
          };
        }),
        estimatedImpact: {
          costChange: -15,
          conversionChange: 25,
          roasChange: 40
        }
      };
      setDryRunResult(mockResult);
      setShowDryRunResult(true);
    } catch (error) {
      console.error('Dry run failed:', error);
    } finally {
      setApplyingRecos(false);
    }
  };

  const handleApply = async () => {
    if (selectedRecos.size === 0) return;
    
    const confirmed = window.confirm(
      `Czy na pewno chcesz wdrożyć ${selectedRecos.size} rekomendacji? Ta operacja wprowadzi zmiany w Google Ads.`
    );
    
    if (!confirmed) return;
    
    setApplyingRecos(true);
    try {
      await onApply(Array.from(selectedRecos), false);
      setSelectedRecos(new Set());
      alert('Rekomendacje zostały wdrożone!');
    } catch (error) {
      console.error('Apply failed:', error);
      alert('Błąd podczas wdrażania rekomendacji');
    } finally {
      setApplyingRecos(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'PROMOTE':
        return <TrendingUp className="w-5 h-5 text-green-400" />;
      case 'TEST':
        return <Activity className="w-5 h-5 text-yellow-400" />;
      case 'EXCLUDE':
        return <Shield className="w-5 h-5 text-red-400" />;
      default:
        return <Zap className="w-5 h-5 text-purple-400" />;
    }
  };

  const getRiskBadge = (risk: string) => {
    const badges = {
      low: { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Niskie ryzyko' },
      medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Średnie ryzyko' },
      high: { bg: 'bg-red-500/20', text: 'text-red-300', label: 'Wysokie ryzyko' }
    };
    const badge = badges[risk] || badges.low;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'bg-green-500';
    if (confidence >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-pulse" />
          <p className="text-white/60">Generowanie rekomendacji AI...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/20 mb-4">
          <Sparkles className="w-8 h-8 text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">Brak rekomendacji</h3>
        <p className="text-white/60 text-sm">
          Uruchom AI Audyt, aby wygenerować inteligentne rekomendacje optymalizacji
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview - DARK THEME */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`cursor-pointer p-6 rounded-2xl bg-white/5 backdrop-blur-xl border transition-all ${
            filter === 'all' ? 'border-purple-500/50 shadow-lg shadow-purple-500/20' : 'border-white/10'
          }`}
          onClick={() => setFilter('all')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/60">Wszystkie</p>
              <p className="text-3xl font-bold text-white mt-1">{recommendations.length}</p>
            </div>
            <div className="p-3 rounded-xl bg-purple-500/20">
              <Zap className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`cursor-pointer p-6 rounded-2xl bg-white/5 backdrop-blur-xl border transition-all ${
            filter === 'promote' ? 'border-green-500/50 shadow-lg shadow-green-500/20' : 'border-white/10'
          }`}
          onClick={() => setFilter('promote')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/60">Do promocji</p>
              <p className="text-3xl font-bold text-green-400 mt-1">{stats.promote}</p>
            </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`cursor-pointer p-6 rounded-2xl bg-white/5 backdrop-blur-xl border transition-all ${
            filter === 'test' ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/20' : 'border-white/10'
          }`}
          onClick={() => setFilter('test')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/60">Do testów</p>
              <p className="text-3xl font-bold text-yellow-400 mt-1">{stats.test}</p>
            </div>
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <Activity className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`cursor-pointer p-6 rounded-2xl bg-white/5 backdrop-blur-xl border transition-all ${
            filter === 'exclude' ? 'border-red-500/50 shadow-lg shadow-red-500/20' : 'border-white/10'
          }`}
          onClick={() => setFilter('exclude')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/60">Do wykluczenia</p>
              <p className="text-3xl font-bold text-red-400 mt-1">{stats.exclude}</p>
            </div>
            <div className="p-3 rounded-xl bg-red-500/20">
              <Shield className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Action Bar - DARK THEME */}
      {recommendations.length > 0 && (
        <div className="p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white text-sm font-medium"
              >
                {selectedRecos.size === filteredRecommendations.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
              </button>
              {selectedRecos.size > 0 && (
                <span className="text-sm text-white/60">
                  Wybrano: {selectedRecos.size} rekomendacji
                </span>
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleDryRun}
                disabled={selectedRecos.size === 0 || applyingRecos}
                className="px-4 py-2 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-blue-400 text-sm font-medium flex items-center gap-2 border border-blue-500/30"
              >
                <Eye className="w-4 h-4" />
                Podgląd zmian
              </button>
              <button
                onClick={handleApply}
                disabled={selectedRecos.size === 0 || applyingRecos}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-white text-sm font-medium flex items-center gap-2 shadow-lg shadow-purple-500/30"
              >
                <PlayCircle className="w-4 h-4" />
                Wdróż wybrane
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations List - DARK THEME */}
      <AnimatePresence>
        <div className="space-y-4">
          {filteredRecommendations.map((reco, index) => (
            <motion.div
              key={reco.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
              className={`relative p-6 rounded-2xl bg-white/5 backdrop-blur-xl border transition-all ${
                selectedRecos.has(reco.id) 
                  ? 'border-purple-500/50 shadow-lg shadow-purple-500/20' 
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="pt-1">
                  <input
                    type="checkbox"
                    checked={selectedRecos.has(reco.id)}
                    onChange={() => handleSelectReco(reco.id)}
                    className="w-5 h-5 rounded border-white/30 bg-white/10 text-purple-500 focus:ring-purple-500/50"
                  />
                </div>

                {/* Icon */}
                <div className="pt-1">
                  {getActionIcon(reco.action)}
                </div>

                {/* Main Content */}
                <div className="flex-1 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      {reco.keyword && (
                        <div>
                          <h3 className="font-semibold text-lg text-white">{reco.keyword.text}</h3>
                          <p className="text-sm text-white/60 mt-1">{reco.keyword.campaignName}</p>
                        </div>
                      )}
                      <p className="text-sm text-white/80 mt-3">{reco.reason}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {getRiskBadge(reco.riskLevel)}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/40">Pewność:</span>
                        <div className="flex items-center gap-1">
                          <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getConfidenceColor(reco.confidence)} transition-all`}
                              style={{ width: `${reco.confidence}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-white/60">{reco.confidence}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Changes */}
                  {reco.changes && reco.changes.length > 0 && (
                    <div className="rounded-xl bg-white/5 p-4 border border-white/10">
                      <p className="text-sm font-medium text-white/80 mb-3">Proponowane zmiany:</p>
                      <div className="space-y-2">
                        {reco.changes.map((change, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="text-white/70">{change.description}</span>
                            {change.currentValue !== undefined && change.newValue !== undefined && (
                              <span className="text-white/50 ml-auto">
                                {typeof change.currentValue === 'object' ? JSON.stringify(change.currentValue) : change.currentValue}
                                {' → '}
                                {typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : change.newValue}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expected Impact */}
                  {reco.expectedImpact && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-white/5">
                          <DollarSign className="w-4 h-4 text-white/40" />
                        </div>
                        <div>
                          <p className="text-xs text-white/40">Zmiana kosztów</p>
                          <p className={`text-sm font-semibold flex items-center gap-1 ${
                            (reco.expectedImpact.costDeltaPct || 0) > 0 ? 'text-red-400' : 'text-green-400'
                          }`}>
                            {(reco.expectedImpact.costDeltaPct || 0) > 0 ? (
                              <ArrowUpRight className="w-3 h-3" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3" />
                            )}
                            {Math.abs(reco.expectedImpact.costDeltaPct || 0)}%
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-white/5">
                          <Target className="w-4 h-4 text-white/40" />
                        </div>
                        <div>
                          <p className="text-xs text-white/40">Zmiana konwersji</p>
                          <p className={`text-sm font-semibold flex items-center gap-1 ${
                            (reco.expectedImpact.convDeltaPct || 0) > 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {(reco.expectedImpact.convDeltaPct || 0) > 0 ? (
                              <ArrowUpRight className="w-3 h-3" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3" />
                            )}
                            {Math.abs(reco.expectedImpact.convDeltaPct || 0)}%
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-white/5">
                          <TrendingDown className="w-4 h-4 text-white/40" />
                        </div>
                        <div>
                          <p className="text-xs text-white/40">Zmiana CPA</p>
                          <p className={`text-sm font-semibold flex items-center gap-1 ${
                            (reco.expectedImpact.cpaDeltaPct || 0) > 0 ? 'text-red-400' : 'text-green-400'
                          }`}>
                            {(reco.expectedImpact.cpaDeltaPct || 0) > 0 ? (
                              <ArrowUpRight className="w-3 h-3" />
                            ) : (
                              <ArrowDownRight className="w-3 h-3" />
                            )}
                            {Math.abs(reco.expectedImpact.cpaDeltaPct || 0)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatePresence>

      {/* Dry Run Result Modal - DARK THEME */}
      <AnimatePresence>
        {showDryRunResult && dryRunResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDryRunResult(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-3xl max-h-[80vh] overflow-auto rounded-2xl bg-slate-900 border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-white">Podgląd zmian (Dry Run)</h2>
                  <button
                    onClick={() => setShowDryRunResult(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5 text-white/60" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-300">Tryb podglądu</p>
                      <p className="text-xs text-blue-300/60 mt-1">
                        To jest tylko symulacja. Żadne zmiany nie zostały jeszcze wprowadzone.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Planowane zmiany:</h3>
                  
                  {dryRunResult.changes?.map((change: any, idx: number) => (
                    <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        {getActionIcon(change.action)}
                        <span className="font-medium text-white">{change.keyword}</span>
                      </div>
                      <div className="space-y-1 ml-7">
                        {change.changes?.map((c: any, i: number) => (
                          <p key={i} className="text-sm text-white/60">• {c.description}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {dryRunResult.estimatedImpact && (
                    <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20">
                      <h4 className="text-sm font-medium text-white/80 mb-3">Przewidywany wpływ:</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-white/40">Koszty</p>
                          <p className="text-lg font-semibold text-green-400">
                            {dryRunResult.estimatedImpact.costChange}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-white/40">Konwersje</p>
                          <p className="text-lg font-semibold text-green-400">
                            +{dryRunResult.estimatedImpact.conversionChange}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-white/40">ROAS</p>
                          <p className="text-lg font-semibold text-green-400">
                            +{dryRunResult.estimatedImpact.roasChange}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    onClick={() => setShowDryRunResult(false)}
                    className="px-6 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white font-medium"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={() => {
                      setShowDryRunResult(false);
                      handleApply();
                    }}
                    className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all text-white font-medium flex items-center gap-2 shadow-lg shadow-purple-500/30"
                  >
                    <Check className="w-4 h-4" />
                    Wdróż zmiany
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecommendationsPanel;
